import { useState } from "react";
import { ChevronDown, ChevronRight, Loader2, RefreshCw, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { isElevatedRole } from "@/lib/roles";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
import { useSupportDepartments, useSyncDepartments } from "@/hooks/useSupportDepartments";
import type { DepartmentSyncResult } from "@/services/supportDepartmentsService";

import { DepartmentSection } from "./DepartmentSection";
import { apiErrorMessage } from "./slackId";

function syncSummary(r: DepartmentSyncResult): string {
  const parts = [
    `${r.active_members ?? 0} active members`,
    `${r.created ?? 0} new`,
    `${r.deactivated ?? 0} deactivated`,
    `${r.slack_resolved ?? 0} Slack ids found`,
  ];
  if (r.slack_not_found) parts.push(`${r.slack_not_found} not found in Slack`);
  if (r.unmapped_roles?.length) parts.push(`unmapped roles: ${r.unmapped_roles.join(", ")}`);
  if (r.lookups_stopped) parts.push("Slack lookups stopped (missing users:read.email scope)");
  return parts.join(" · ");
}

/** Support departments admin: who each department's tickets mention, and the
 *  Slack ids behind them. super_admin / platform_owner only. Collapsed by
 *  default; nothing is fetched until it is opened. */
export function DepartmentsPanel() {
  const platformRole = useWorkspaceStore((s) => s.userProfile?.platform_role);
  const [open, setOpen] = useState(false);
  const elevated = isElevatedRole(platformRole);
  const query = useSupportDepartments(open && elevated);
  const sync = useSyncDepartments();

  if (!elevated) return null;

  return (
    <div className="mb-5 rounded-lg border border-border bg-muted/30">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-foreground"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <Users className="h-4 w-4 text-coral" />
        Departments
        {query.data ? (
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            Routing {query.data.routing_enabled ? "on" : "off"}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="space-y-3 border-t border-border px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={sync.isPending}
              onClick={() => sync.mutate()}
            >
              {sync.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-1 h-4 w-4" />
              )}
              Refresh from Teamwork
            </Button>
            {sync.isSuccess ? (
              <p role="status" className="text-sm text-muted-foreground">
                {syncSummary(sync.data)}
              </p>
            ) : null}
            {sync.isError ? (
              <p role="alert" className="text-sm text-destructive">
                Refresh failed: {apiErrorMessage(sync.error, "the Teamwork sync did not complete.")}
              </p>
            ) : null}
          </div>

          {query.isLoading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading departments…
            </div>
          ) : query.isError ? (
            <p className="py-4 text-sm text-destructive">
              Couldn't load departments: {apiErrorMessage(query.error, "please try again.")}
            </p>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              {query.data?.departments.map((d) => (
                <DepartmentSection key={d.key} department={d} />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
